import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière est l'histoire du tampon.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'videoloop',
  family: 'shader',
  danceability: 0.84,
  requiresTier: 1,
  energyLevel: 0.78,
  bpmMin: 110,
  bpmMax: 170,
  movement: 0.80,
  tonality: 0.45,
  enabled: true
};

// Videoloop : la boucle vidéo ANALOGIQUE - une caméra filme son propre
// moniteur, l'image se répète à l'infini en tunnels et en spirales, avec la
// dérive de couleur, le flou et le grain des tubes cathodiques. LOOK VDJ
// party. Techno, acid, psytrance, krautrock électronique, IDM.
//
// LA BOUCLE (FeedbackBuffer d'Echo, R15), UNE passe par frame :
//   le moniteur LE PLUS GRAND EST L'ÉCRAN : son cadre longe les bords de
//               l'image ; la caméra q = R(rot) . p . zoom + décalage (p centré
//               sur l'écran, R19) filme l'image précédente, cadre compris :
//               le moniteur se répète dans le moniteur.
//   la vidéo    flou horizontal SYMÉTRIQUE 0,25 / 0,5 / 0,25 (rien d'autre
//               qui décale : un décalage refait à chaque passage s'étire en
//               bandes) ; la TEINTE tourne en YIQ (I et Q d'un angle de 6 à
//               12° par passage, Y inchangé : luminance et saturation
//               gardées, la roue en 30 à 60 passages) ; une petite dérive de
//               sous-porteuse ajoute un peu de I x Y à chaque passage (sans
//               elle, une graine BLANCHE n'a pas de teinte à faire tourner) ;
//               contraste sur Y SEULEMENT (par canal il sature le canal
//               dominant et tue la teinte).
//   la stabilité les gardes d'Echo (décroissance en demi-vie, plancher,
//               plafond 0,92, dither masqué) + son gouverneur de luminance +
//               un gouverneur de SATURATION (moyenne > 0,85 : I et Q x 0,98).
// Les boutons (zoom, rotation, décalage) sont donnés PAR FRAME À 60 FPS et
// mis à l'échelle de dt.
//
// LA GRAINE ORBITE autour du centre de l'écran (R19), sur un cercle de 0,28
// hauteur - hors de la pochette quand elle est au centre : la miniature de la
// pochette (18 % de la hauteur), un tour en 4 mesures, et 2 ou 3 formes
// saturées (cercle, éclair, étoile) sur la même orbite ; sur chaque temps une
// forme nouvelle apparaît en flash. Comme elles bougent, la boucle en fait des
// spirales et des traînées arc-en-ciel.
//
// LE RYTHME : temps = +3° de rotation par passage pendant le premier huitième
// du temps ; mesure = le zoom passe de l'autre côté de 1 ; phrase =
// préréglage suivant (TUNNEL, SPIRAL, GALAXY, FLOWER, DRIFT - tous tournent
// sauf TUNNEL) ; montée = teinte et rotation accélèrent ; drop = boucle
// débranchée : 2 frames de neige (la 1re presque noire), puis GALAXY.
//
// L'AFFICHAGE, et lui seul : courbure de tube NORMALISÉE (les coins restent
// dans l'image, aucune marge noire), rouge / bleu décalés d'un texel, lignes
// de balayage à 8 %, grain fin animé, masque de pochette (R19).
//
// Debug : ?vlpreset=tunnel|spiral|galaxy|flower|drift, ?vlzoom=<f>,
// ?vlrot=<degrés par frame>.

// the knobs, per frame at 60 fps: zoom > 1 = the camera backs off (the
// monitor shrinks: a tunnel going in)
// hue: degrees of YIQ rotation per pass (6 to 12: the wheel in 30 to 60
// passes). Every preset turns but TUNNEL.
export const PRESETS = {
  tunnel: { zoom: 1.045, rot: 0.0, off: [0, 0], hue: 10 },
  spiral: { zoom: 1.035, rot: 4.0, off: [0, 0], hue: 11 },
  galaxy: { zoom: 1.025, rot: 6.5, off: [0.012, 0.004], hue: 12 },
  flower: { zoom: 0.975, rot: 5.0, off: [0, 0], hue: 12 },
  drift: { zoom: 1.012, rot: 1.6, off: [0.014, 0.007], hue: 8 }
};
const CHROMA_PUSH = 0.08;          // the sub-carrier's drift per pass (x Y, along I): a white seed gets a hue
const SAT_MAX = 0.85;              // the saturation governor's threshold
const SHAPE_BASE = 0.45, SHAPE_FLASH = 0.9;
const PRESET_ORDER = ['tunnel', 'spiral', 'galaxy', 'flower', 'drift'];
const INTENSE = 'galaxy';
const KICK_ROT = 3.0;              // degrees per pass, for an eighth of a beat
const HALF_LIFE_S = 0.7;           // the loop's decay as a half-life (R15): a repetition still visible has turned the whole wheel
const DECAY_MIN = 0.80, DECAY_MAX = 0.985;
const FLOOR_SUB = 0.002;          // half Echo's: the deep repetitions must live long enough to turn the colour wheel (soak: no grey veil)
const CEILING = 0.92;
const GOVERN_EVERY = 30, GOVERN_HIGH = 0.32, GOVERN_RESUME = 0.24, GOVERN_LOW = 0.05, GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const SAFE_FLOOR = 0.5;
const NO_BPM = 124;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const TIER = {
  0: { scale: 0.25 },
  1: { scale: 1 / 3 },
  2: { scale: 0.5 }
};
const FALLBACK = [0x39a0ff, 0xff3ba7, 0xffd23b];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// the loop: camera, monitor, analog wear, guards, and the seed
const feedbackFragment = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform sampler2D uArt;
  uniform float uHasArt;
  uniform float uAspect;           // the buffer's
  uniform vec2 uTexel;             // one buffer texel
  uniform float uZoom;
  uniform float uRot;              // radians this pass
  uniform vec2 uOff;
  uniform vec2 uHueCS;             // cos, sin of the YIQ hue rotation this pass
  uniform float uChromaPush;       // the sub-carrier's drift: chroma added along I, x Y
  uniform float uSatK;             // the saturation governor (0.98 when the loop runs hot)
  uniform float uDecay;
  uniform float uSeedFrame;
  uniform float uSnow;             // > 0: the loop is unplugged, analog snow
  uniform vec3 uCol0;
  uniform vec3 uCol1;
  uniform vec3 uCol2;
  uniform float uOrbit;            // the orbit's angle
  uniform float uArtOn;
  uniform vec4 uShapeType;         // per slot: 0 circle, 1 bolt, 2 star (w unused)
  uniform vec4 uShapeAmp;          // per slot: brightness (the beat's flash on top)
  varying vec2 vUv;

  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float rrect(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
  float seg(vec2 p, vec2 a, vec2 b) { vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
  // a five-pointed star (Inigo Quilez's sdStar5)
  float star5(vec2 p, float r, float rf) {
    const vec2 k1 = vec2(0.809016994375, -0.587785252292);
    const vec2 k2 = vec2(-k1.x, k1.y);
    p.x = abs(p.x);
    p -= 2.0 * max(dot(k1, p), 0.0) * k1;
    p -= 2.0 * max(dot(k2, p), 0.0) * k2;
    p.x = abs(p.x);
    p.y -= r;
    vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0.0, 1.0);
    float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
    return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
  }
  // one shape of the seed, as a light (1 at its stroke)
  float shape(vec2 p, float type, float px) {
    float d;
    if (type < 0.5) d = abs(length(p) - 0.035);
    else if (type < 1.5) d = min(min(seg(p, vec2(-0.03, 0.045), vec2(0.008, 0.006)), seg(p, vec2(0.008, 0.006), vec2(-0.010, -0.004))), seg(p, vec2(-0.010, -0.004), vec2(0.03, -0.045)));
    else d = abs(star5(p, 0.042, 0.45));
    return 1.0 - smoothstep(0.8 * px, 2.2 * px, d);
  }
  const mat3 RGB2YIQ = mat3(0.299, 0.596, 0.211, 0.587, -0.274, -0.523, 0.114, -0.322, 0.312);
  const mat3 YIQ2RGB = mat3(1.0, 1.0, 1.0, 0.956, -0.272, -1.106, 0.621, -0.647, 1.703);

  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);         // height units, centred (R19)
    float px = uTexel.y;
    if (uSnow > 0.0) {
      float n = hash(floor(vUv / uTexel) + uSeedFrame * 7.31);
      gl_FragColor = vec4(vec3(n * n * uSnow), 1.0);
      return;
    }
    // THE MONITOR IS THE SCREEN: its bezel runs along the frame's edges (the
    // largest monitor coincides with the screen); the camera films it
    vec2 hb = vec2(0.5 * uAspect, 0.5);
    float dScreen = rrect(p, hb - 0.022, 0.05);
    vec3 col = vec3(0.0);
    if (dScreen < 0.0) {
      float c = cos(uRot), s = sin(uRot);
      vec2 q = vec2(c * p.x - s * p.y, s * p.x + c * p.y) * uZoom + uOff;
      vec2 uv = q / vec2(uAspect, 1.0) + 0.5;
      if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0) {
        // limited bandwidth: a SYMMETRIC 3-tap blur, 0.25 / 0.5 / 0.25 - no
        // channel shift in the loop (re-applied every pass it smears into
        // horizontal streaks; the tube's misregistration lives in the display)
        vec2 t = vec2(uTexel.x, 0.0);
        col = 0.25 * texture2D(uPrev, uv - t).rgb + 0.5 * texture2D(uPrev, uv).rgb + 0.25 * texture2D(uPrev, uv + t).rgb;
        // THE HUE turns in YIQ: I and Q rotate, Y (the luminance) and the
        // saturation stay - the repetitions go round the colour wheel
        vec3 yiq = RGB2YIQ * col;
        yiq.y += uChromaPush * yiq.x;                  // the sub-carrier's drift: even white gets a hue
        yiq.yz = vec2(uHueCS.x * yiq.y - uHueCS.y * yiq.z, uHueCS.y * yiq.y + uHueCS.x * yiq.z) * uSatK;
        // contrast on the LUMINANCE only (per channel it saturates the
        // dominant channel and kills the hue)
        float Y = yiq.x;
        yiq.x = Y + 0.10 * Y * (1.0 - Y) * (Y - 0.25);
        col = YIQ2RGB * yiq;
      }
      col *= 0.85 + 0.15 * smoothstep(0.0, -0.02, dScreen);   // the screen's corners darken
    } else {
      // the bezel: black, a thin lip of the accent toward the screen
      float lip = exp(-abs(dScreen) / (1.2 * px));
      col = uCol0 * 0.45 * lip;
    }
    // THE GUARDS (Echo): gain < 1, a floor, a ceiling - no negative channel
    col = max(col * uDecay - vec3(${FLOOR_SUB.toFixed(4)}), 0.0);
    col = min(col, vec3(${CEILING.toFixed(2)}));
    // dither on the signal only (R15: the quantisation steps band otherwise)
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeedFrame, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    col += dith * step(3.5 / 255.0, max(col.r, max(col.g, col.b)));

    // THE SEED: elements ORBITING the centre of the screen (R19), r = 0.28 x
    // the height - outside the cover's zone when the cover is centred. They
    // move: the loop turns them into spirals and rainbow trails.
    const float R = 0.28;
    vec2 ca = R * vec2(cos(uOrbit), sin(uOrbit));
    if (uArtOn > 0.5) {
      vec2 ap = (p - ca) / 0.18 + 0.5;
      if (ap.x > 0.0 && ap.x < 1.0 && ap.y > 0.0 && ap.y < 1.0) {
        vec3 art = uHasArt > 0.5 ? texture2D(uArt, ap).rgb : mix(uCol0, uCol1, ap.x);
        col = max(col, art * 0.45);     // under the ceiling: a bright seed keeps room for its chroma
      }
      float fr = abs(rrect(p - ca, vec2(0.092), 0.006));
      col += uCol0 * 0.5 * (1.0 - smoothstep(0.6 * px, 1.8 * px, fr));
    }
    for (int k = 0; k < 3; k++) {
      float a = uOrbit + 6.2831853 * float(k + 1) / 4.0;
      vec2 c = R * vec2(cos(a), sin(a));
      float ty = k == 0 ? uShapeType.x : (k == 1 ? uShapeType.y : uShapeType.z);
      float amp = k == 0 ? uShapeAmp.x : (k == 1 ? uShapeAmp.y : uShapeAmp.z);
      if (amp <= 0.0) continue;
      vec3 sc = k == 0 ? uCol1 : (k == 1 ? uCol2 : uCol0);
      col += mix(sc, vec3(1.0), 0.25) * amp * shape(p - c, ty, px);
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// display: the tube's curvature (normalised: the corners stay in the picture,
// no dark margin), its red / blue misregistration, scanlines, grain - here
// ONLY, never in the loop
const displayFragment = `
  precision highp float;
  uniform sampler2D uBuffer;
  uniform float uAspect;
  uniform float uTime;
  uniform float uLines;            // the display's height in px (scanlines)
  uniform vec2 uTexelD;            // one buffer texel
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  void main() {
    vec2 c = (vUv - 0.5) * vec2(uAspect, 1.0);
    float rmax2 = 0.25 * (uAspect * uAspect + 1.0);
    vec2 uv = c * (1.0 + 0.035 * dot(c, c)) / (1.0 + 0.035 * rmax2) / vec2(uAspect, 1.0) + 0.5;
    vec3 col;
    col.r = texture2D(uBuffer, uv + vec2(uTexelD.x, 0.0)).r;
    col.g = texture2D(uBuffer, uv).g;
    col.b = texture2D(uBuffer, uv - vec2(uTexelD.x, 0.0)).b;
    col *= 0.92 + 0.08 * (0.5 + 0.5 * cos(vUv.y * uLines * 3.14159265));
    float g = hash(mod(floor(gl_FragCoord.xy), 512.0) + floor(mod(uTime * 60.0, 97.0)) * 13.0) - 0.5;
    col += g * 0.025 * (0.3 + col);
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;
const lumFragment = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }
`;

export class VideoloopVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const pr = urlParam('vlpreset');
    this._forcePreset = PRESETS[pr] ? pr : null;
    const z = parseFloat(urlParam('vlzoom')); this._forceZoom = Number.isFinite(z) ? z : null;
    const r = parseFloat(urlParam('vlrot')); this._forceRot = Number.isFinite(r) ? r : null;
    // the bloom only for what the seed injects: the loop sits under it
    this._post = new VdjPost({ bloomStrength: 0.25, bloomThreshold: 0.93, grain: 0.0, vignette: 0.40, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._buffer = new FeedbackBuffer({ scale: this._layout.scale });
    this._fu = {
      uPrev: { value: null }, uArt: { value: null }, uHasArt: { value: 0 },
      uAspect: { value: 16 / 9 }, uTexel: { value: new THREE.Vector2(1 / 640, 1 / 360) },
      uZoom: { value: 1 }, uRot: { value: 0 }, uOff: { value: new THREE.Vector2() },
      uHueCS: { value: new THREE.Vector2(1, 0) }, uChromaPush: { value: CHROMA_PUSH }, uSatK: { value: 1 }, uDecay: { value: 0.95 },
      uSeedFrame: { value: 0 }, uSnow: { value: 0 },
      uCol0: { value: new THREE.Color(1, 1, 1) }, uCol1: { value: new THREE.Color(1, 1, 1) }, uCol2: { value: new THREE.Color(1, 1, 1) },
      uOrbit: { value: 0 }, uArtOn: { value: 1 },
      uShapeType: { value: new THREE.Vector4(0, 1, 2, 0) }, uShapeAmp: { value: new THREE.Vector4(SHAPE_BASE, SHAPE_BASE, 0, 0) }
    };
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    this._fbScene = new THREE.Scene();
    this._fbMat = new THREE.ShaderMaterial({ uniforms: this._fu, vertexShader: quadVertex, fragmentShader: feedbackFragment, depthTest: false, depthWrite: false });
    const fq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._fbMat); fq.frustumCulled = false; this._fbScene.add(fq); this._fbQuad = fq;
    // the governor's 8 x 8 readback
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._lumU = { uBuffer: { value: null } };
    this._lumScene = new THREE.Scene();
    this._lumMat = new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: quadVertex, fragmentShader: lumFragment, depthTest: false, depthWrite: false });
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._lumMat); lq.frustumCulled = false; this._lumScene.add(lq); this._lumQuad = lq;
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);
    // display
    this._du = {
      uBuffer: { value: null }, uAspect: safeZoneUniforms.uAspect, uTime: { value: 0 }, uLines: { value: 720 }, uTexelD: { value: new THREE.Vector2(1 / 640, 1 / 360) },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._dispMat = new THREE.ShaderMaterial({ uniforms: this._du, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._dispMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._display);

    // state
    this._time = 0; this._beats = 0; this._beatPeriod = 60 / NO_BPM; this._dt = 1 / 60;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null; this._free = 0;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._presetName = this._forcePreset || 'tunnel'; this._presetIdx = PRESET_ORDER.indexOf(this._presetName);
    this._zoomSide = 1;              // the bar flips the zoom across 1
    this._kickUntil = -1; this._build = 0;
    this._decay = 0.95; this._governorHot = false; this._frame = 0; this._lastLum = 0;
    this._hue = 0;
    this._slots = 3; this._flashAt = null; this._flashSlot = null; this._energy = null; this._lastSat = 0;
    this._snowFrames = 0;
    this._palette = FALLBACK.map((c) => vdjColor(c));
    this._paletteRef = undefined; this._artUrl = undefined; this._artTex = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastDropKey = null;
    this._seed = 424242;
    this._paused = false;
  }

  _rnd() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK;
    this._palette = [0, 1, 2].map((i) => vdjColor(v[i % v.length]));
  }
  _loadArt(profile) {
    if (profile.artworkUrl === this._artUrl) return;
    this._artUrl = profile.artworkUrl;
    if (this._artTex) { this._artTex.dispose(); this._artTex = null; }
    this._fu.uHasArt.value = 0;
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._artUrl) { tex.dispose(); return; }
      self._artTex = tex; self._fu.uArt.value = tex; self._fu.uHasArt.value = 1;
    }, () => {});
  }

  // --- the passes, from the display quad's onBeforeRender (Echo's pattern) ---
  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    renderer.getDrawingBufferSize(this._size);
    this._du.uLines.value = this._size.y;
    if (rebuilt) {
      this._fu.uAspect.value = this._buffer.width / Math.max(1, this._buffer.height);
      this._fu.uTexel.value.set(1 / this._buffer.width, 1 / this._buffer.height);
      this._du.uTexelD.value.set(1 / this._buffer.width, 1 / this._buffer.height);
    }
    if (this._paused) { this._du.uBuffer.value = this._buffer.read.texture; return; }
    const prev = renderer.getRenderTarget(), prevAuto = renderer.autoClear;
    const u = this._fu;
    u.uPrev.value = this._buffer.read.texture;
    u.uSeedFrame.value = (this._frame * 13.13) % 64;
    u.uSnow.value = 0;
    if (this._snowFrames > 0) { u.uSnow.value = this._snowFrames === 2 ? 0.25 : 0.55; this._snowFrames--; }
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;
    renderer.render(this._fbScene, this._ortho);
    this._frame++;
    if (this._frame % GOVERN_EVERY === 0) this._govern(renderer);
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    this._buffer.swap();
    this._du.uBuffer.value = this._buffer.read.texture;
  }

  // Echo's governor: the buffer's real mean luminance steers the decay
  _govern(renderer) {
    this._lumU.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;
    // the saturation governor: the mean saturation of the lit regions; over
    // 0.85, I and Q x 0.98 in the loop until it falls back
    let ss = 0, sn = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      const r = this._lumPixels[i], g = this._lumPixels[i + 1], b = this._lumPixels[i + 2], mx = Math.max(r, g, b);
      if (mx < 12) continue;
      ss += (mx - Math.min(r, g, b)) / mx; sn++;
    }
    this._lastSat = sn ? ss / sn : 0;
    this._fu.uSatK.value = this._lastSat > SAT_MAX ? 0.98 : 1.0;
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
      else this._governorHot = false;
    } else if (lum < GOVERN_LOW) this._decay = Math.min(this._decayCeil(), this._decay + GOVERN_STEP);
    this._decay = Math.min(this._decay, this._decayCeil());
  }
  // the half-life's decay for this frame (R15), the governor's ceiling
  _decayCeil() { return Math.min(DECAY_MAX, Math.pow(0.5, this._dt / HALF_LIFE_S)); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._loadArt(profile); this._dropIdx = 0; this._lastNowMs = null; }
  onPlayPause(state) { this._paused = state !== 'PLAYING'; }

  // --- rhythm -------------------------------------------------------------------
  _onBeat() {
    this._beatCount++;
    this._kickUntil = this._time + this._beatPeriod / 8;      // the spiral twists, by jolts
    // a NEW shape on the orbit: one slot turns into another shape, in a flash
    const slot = this._beatCount % this._slots;
    const t = this._fu.uShapeType.value;
    const nt = (Math.floor(this._rnd() * 2) + 1 + [t.x, t.y, t.z][slot]) % 3;
    if (slot === 0) t.x = nt; else if (slot === 1) t.y = nt; else t.z = nt;
    this._flashSlot = slot; this._flashAt = this._time;
  }
  _onBar() { this._barCount++; this._zoomSide = -this._zoomSide; }
  _onPhrase() {
    this._phraseCount++;
    if (!this._forcePreset) { this._presetIdx = (this._presetIdx + 1) % PRESET_ORDER.length; this._presetName = PRESET_ORDER[this._presetIdx]; }
  }
  _onDrop() {
    // unplugged: two frames of snow (the first almost black), then plugged
    // back on the most intense preset
    if (this._comfort.requestFlash(this._time)) this._snowFrames = 2;
    if (!this._forcePreset) { this._presetName = INTENSE; this._presetIdx = PRESET_ORDER.indexOf(INTENSE); }
  }

  update(deltaTime, profile) {
    this._loadArt(profile);
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;                          // the loop holds (the display re-shows the last frame)
    const dt = Math.min(Math.max(deltaTime, 1 / 240), 0.05);
    this._dt = dt;
    this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / bpm;
    const en = director && director.intensity != null ? clamp01(director.intensity) : 0.6;
    this._energy = (this._energy == null ? en : this._energy + (en - this._energy) * Math.min(1, dt / 1.5));
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else { this._free += dt; if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; } }
    this._beats += dt / this._beatPeriod;
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director && director.mode === 'build' ? 0.5 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.5);
    if (beatEdge) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 1 && this._beatCount > 1) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 1 && this._beatCount > 1) this._onPhrase();
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    } else if (beatPhase != null && this._tickDrops(profile)) drop = true;
    if (drop) this._onDrop();

    // --- the knobs, per frame at 60 fps, scaled to dt
    const P = PRESETS[this._presetName], k60 = dt * 60;
    const boost = 1 + this._build;                     // the build: the loop races
    let zoom = this._forceZoom != null ? this._forceZoom : P.zoom;
    if (this._forceZoom == null && this._zoomSide < 0) zoom = 1 / zoom;      // the bar: the tunnel turns inside out
    let rotDeg = (this._forceRot != null ? this._forceRot : P.rot) * boost;
    // the jolt: the first eighth of each beat - from the grid's own phase when
    // there is one (a duration from the bpm drifts off the grid's beats)
    const kickOn = beatPhase != null ? (beatPhase < 0.125 && this._beatCount > 0) : this._time < this._kickUntil;
    this._kickOn = kickOn;
    if (kickOn) rotDeg += KICK_ROT;
    const u = this._fu;
    u.uZoom.value = Math.pow(zoom, k60);
    u.uRot.value = rotDeg * Math.PI / 180 * k60;
    u.uOff.value.set(P.off[0] * k60, P.off[1] * k60);
    // the hue: YIQ rotation, 6 to 12 degrees a pass (x1.5 in the build, 18 max)
    const hueDeg = Math.min(18, P.hue * (1 + 0.5 * this._build));
    const ha = hueDeg * Math.PI / 180 * k60;
    u.uHueCS.value.set(Math.cos(ha), Math.sin(ha));
    u.uChromaPush.value = CHROMA_PUSH * k60;
    u.uDecay.value = Math.min(this._decay, this._decayCeil());
    // the seed: the cover's miniature and 2 or 3 shapes on an orbit of 0.28 H,
    // one turn in 4 bars
    u.uOrbit.value = this._beats / 16 * Math.PI * 2;
    u.uCol0.value.copy(this._palette[this._phraseCount % 3]);
    u.uCol1.value.copy(this._palette[(this._phraseCount + 1) % 3]);
    u.uCol2.value.copy(this._palette[(this._phraseCount + 2) % 3]);
    this._slots = this._energy > 0.55 ? 3 : 2;
    const fl = this._flashAt != null ? Math.max(0, 1 - (this._time - this._flashAt) / (0.25 * this._beatPeriod)) : 0;
    const amps = [SHAPE_BASE, SHAPE_BASE, this._slots > 2 ? SHAPE_BASE : 0];
    if (fl > 0 && this._flashSlot != null) amps[this._flashSlot] += SHAPE_FLASH * fl;
    u.uShapeAmp.value.set(amps[0], amps[1], amps[2], 0);
    this._du.uTime.value = this._time;
    this._post.update(vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod));
  }

  get probe() {
    return {
      t: +this._time.toFixed(2), preset: this._presetName, zoom: +this._fu.uZoom.value.toFixed(4), rotDeg: +(this._fu.uRot.value * 180 / Math.PI).toFixed(2),
      kick: !!this._kickOn, side: this._zoomSide, sat: +(this._lastSat || 0).toFixed(2), satK: this._fu.uSatK.value, decay: +this._decay.toFixed(3), lum: +this._lastLum.toFixed(3),
      hot: this._governorHot, snow: this._snowFrames, build: +this._build.toFixed(2), frame: this._frame
    };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._dispMat.dispose(); }
    if (this._fbQuad) { this._fbQuad.geometry.dispose(); this._fbMat.dispose(); }
    if (this._lumQuad) { this._lumQuad.geometry.dispose(); this._lumMat.dispose(); }
    if (this._lumTarget) this._lumTarget.dispose();
    if (this._buffer) this._buffer.dispose();
    if (this._artTex) { this._artTex.dispose(); this._artTex = null; }
    this._scene = null;
  }
}
